import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  UserGroupIcon, 
  DashboardSquare01Icon, 
  Logout01Icon,
  Sun01Icon,
  Moon02Icon,
  Home01Icon,
  Clock01Icon,
  UserCircleIcon,
  HelpCircleIcon,
  Money01Icon
} from '@hugeicons/core-free-icons';
import BottomNavbar from './BottomNavbar';
import { useAuth } from '../contexts/AuthContext';
import BalanceWidget from './BalanceWidget';
import MobileMenu from './MobileMenu';
import { api } from '../utils/api';

const AgentNavbar = ({ isDark, toggleTheme, requestCount, payoutCount }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: DashboardSquare01Icon },
    { name: 'Direct Orders', path: '/requests', icon: Home01Icon },
    { name: 'Clients', path: '/clients', icon: UserGroupIcon },
    { name: 'Commissions', path: '/commissions', icon: Money01Icon },
    { name: 'History', path: '/history', icon: Clock01Icon },
  ];

  return (
    <nav className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md border-b border-light-gray/20 dark:border-dark-border/20 sticky top-0 z-50 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/dashboard" className="flex-shrink-0 flex items-center gap-3">
              <img src="/Safzi-Logo.png" alt="Safzi" className="h-8 w-auto" />
              <span className="text-2xl font-bold text-soft-green tracking-tight hidden sm:block font-mono">SAFZI</span>
            </Link>
            <div className="hidden lg:ml-8 lg:flex lg:space-x-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`inline-flex items-center px-4 py-2 rounded-3xl text-sm font-bold transition-all duration-300 relative ${
                      isActive
                        ? 'bg-soft-green/10 text-soft-green dark:text-light-mint shadow-sm'
                        : 'text-muted-gray hover:bg-gray-100/80 dark:hover:bg-dark-surface/50 hover:text-charcoal dark:hover:text-white'
                    }`}
                  >
                    <HugeiconsIcon icon={Icon} size={18} className="mr-2" />
                    {item.name}
                    {item.name === 'Direct Orders' && requestCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white shadow-lg border-2 border-white dark:border-dark-surface">
                        {requestCount}
                      </span>
                    )}
                    {item.name === 'Commissions' && payoutCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white shadow-lg border-2 border-white dark:border-dark-surface">
                        {payoutCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <BalanceWidget />
            
            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-2">
              <Link to="/support" className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 hover:text-soft-green transition-all" title="Support">
                <HugeiconsIcon icon={HelpCircleIcon} size={20} className="dark:text-gray-300" />
              </Link>
              <button 
                onClick={toggleTheme}
                className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 transform hover:-translate-y-1 hover:shadow-float transition-all duration-300 ease-in-out"
              >
                <HugeiconsIcon icon={isDark ? Sun01Icon : Moon02Icon} size={20} className="dark:text-gray-300" />
              </button>
              <Link to="/profile" className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 transform hover:-translate-y-1 hover:shadow-float transition-all duration-300 ease-in-out">
                <HugeiconsIcon icon={UserCircleIcon} size={20} className="dark:text-gray-300" />
              </Link>
              <button onClick={handleLogout} className="p-2.5 rounded-full text-muted-gray hover:bg-off-white/80 dark:hover:bg-dark-surface/50 transform hover:-translate-y-1 hover:shadow-float transition-all duration-300 ease-in-out">
                <HugeiconsIcon icon={Logout01Icon} size={20} className="dark:text-gray-300" />
              </button>
            </div>

            {/* Mobile Menu */}
            <MobileMenu isDark={isDark} toggleTheme={toggleTheme} />
          </div>
        </div>
      </div>
    </nav>
  );
};

const AgentLayout = ({ isDark, toggleTheme }) => {
  const [requestCount, setRequestCount] = React.useState(0);
  const [payoutCount, setPayoutCount] = React.useState(0);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [ordersRes, commissionsRes] = await Promise.all([
          api.get('/agent/orders-with-transactions'),
          api.get('/agent/commissions?status=pending')
        ]);

        // Process orders count
        const orders = ordersRes.data?.orders || [];
        const flattened = orders.flatMap(o => (o.transactions || []).map(tx => ({ ...tx, order_type: o.transaction_type })));
        const actionable = flattened.filter(tx => {
          if (['pending', 'pending_verification', 'processing'].includes(tx.status)) return true;
          if (tx.status === 'verified' && tx.order_type === 'withdrawal') return true;
          return false;
        });
        setRequestCount(actionable.length);

        // Process payout count - handle pagination or nested data
        // API returns: { data: { payouts: [...] } } or { data: [...] }
        const payouts = commissionsRes.data?.payouts || commissionsRes.data?.data || commissionsRes.data || [];
        setPayoutCount(Array.isArray(payouts) ? payouts.length : 0);
      } catch (err) { console.warn('Failed to fetch counts:', err); }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-off-white dark:bg-dark-bg transition-colors duration-200">
      <AgentNavbar isDark={isDark} toggleTheme={toggleTheme} requestCount={requestCount} payoutCount={payoutCount} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        <Outlet />
      </main>
      <BottomNavbar pendingCount={requestCount} payoutCount={payoutCount} />
    </div>
  );
};

export default AgentLayout;